import { useCallback, useEffect, useMemo, useState } from "react";
import { FormControl, FormField, FormItem, FormLabel } from "../../../components/ui/form";
import { Popover, PopoverContent, PopoverTrigger } from "../../../components/ui/popover";
import { emptyYachtsFiltersForm, YachtsFiltersFormT } from "./YachtsFiltersForm/yachts-filters.schema";
import { useOffersStore } from "../../../stores/offersStore";
import { useForm, useFormContext } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Form } from "@/src/components/ui/form";
import { defaultDates } from "../../../components/ui/date-range-picker";
import { ScoreSelect } from "../../../components/ui/score-select";
import { Checkbox } from "../../../components/ui/checkbox";
import { Calendar } from "@/src/components/ui/calendar";
import { DateRange } from "react-day-picker";
import { format, parseISO } from "date-fns";
import { boatsFetch } from "../../../lib/boatsFetch";
import { findCommonFormProps } from "./YachtsFiltersForm/YachtsFiltersForm";
import { XCircleIcon } from "lucide-react";
import { ComboboxYachtsSearch } from "@/src/components/ui/combobox-yachts-search";

// Curated shortcuts shown in the "Where" search. names[] are the EXACT search
// entities (verified against /search/locations — note the real-world quirks:
// trailing space on "Southern Ionian ", the "Ionain" typo, "Split Region"). A chip
// can seed more than one region (Saronic + Cyclades) since the field is an array.
const POPULAR_SEARCHES: { label: string; entityType: "region" | "country"; names: string[] }[] = [
  { label: "Southern Ionian", entityType: "region", names: ["Southern Ionian "] },
  { label: "Split", entityType: "region", names: ["Split Region"] },
  { label: "Saronic / Cyclades", entityType: "region", names: ["Athens / Saronic Gulf", "Cyclades"] },
  { label: "Corfu", entityType: "region", names: ["Corfu (Northern Ionain)"] },
  { label: "Amalfi", entityType: "region", names: ["Amalfi Coast"] },
  { label: "Sardinia", entityType: "region", names: ["Sardinia / Corsica"] },
  { label: "Dodecanese", entityType: "region", names: ["Dodecanese"] },
  { label: "Croatia", entityType: "country", names: ["Croatia"] },
  { label: "Greece", entityType: "country", names: ["Greece"] },
  { label: "Italy", entityType: "country", names: ["Italy"] },
  { label: "France", entityType: "country", names: ["France"] },
  { label: "British Virgin Islands", entityType: "country", names: ["British Virgin Islands"] },
];

export const SearchBar = () => {
  const { yachtsFilters, setYachtsFilters } = useOffersStore();
  const form = useForm<YachtsFiltersFormT>({
    resolver: zodResolver(YachtsFiltersFormT),
    defaultValues: emptyYachtsFiltersForm,
    ...(yachtsFilters ? { values: { ...emptyYachtsFiltersForm, ...yachtsFilters } } : {}),
    resetOptions: {
      keepDefaultValues: true,
    },
    mode: "onChange",
  });

  const countryField = form.watch("country");
  const locationField = form.watch("location");
  const townField = form.watch("town");
  const regionField = form.watch("region");
  const tlrField = form.watch("tlr");
  const boatTypeField = form.watch("boatType");
  const checkOutField = form.watch("checkIn");
  const checkInField = form.watch("checkOut");
  const peopleField = form.watch("people");
  const modelField = form.watch("model");
  const cabinsMinField = form.watch("cabinsMin");
  const cabinsMaxField = form.watch("cabinsMax");
  const bathroomsMinField = form.watch("bathroomsMin");
  const bathroomsMaxField = form.watch("bathroomsMax");

  const onSubmit = useCallback(
    form.handleSubmit(
      (data) => {
        const dirtyFields = findCommonFormProps(data, form.formState.dirtyFields);
        // 0 means "no bound" for these steppers — a 0 max would otherwise
        // filter to `value <= 0` server-side and return nothing.
        (["cabinsMin", "cabinsMax", "bathroomsMin", "bathroomsMax"] as const).forEach((k) => {
          if (!dirtyFields[k]) delete dirtyFields[k];
        });
        setYachtsFilters(dirtyFields);
      },
      () => {
        const values = form.getValues();
        const issues = YachtsFiltersFormT.safeParse(values).error?.issues ?? [];
        console.log(issues, "errors");
      },
    ),
    [form],
  );

  useEffect(() => {
    form.handleSubmit(() => onSubmit())();
  }, [
    countryField,
    locationField,
    regionField,
    tlrField,
    boatTypeField,
    peopleField,
    checkInField,
    checkOutField,
    modelField,
    townField,
    cabinsMinField,
    cabinsMaxField,
    bathroomsMinField,
    bathroomsMaxField,
  ]);

  return (
    <div className="bg-blue-50 border border-blue-100 rounded-lg px-6 py-4 mb-8">
      <Form {...form}>
        <div className="flex justify-between items-center gap-x-8">
          <SearchField />
          <DateField />
          <GuestsField />
          <YachtTypesField />
        </div>
      </Form>
    </div>
  );
};

const SearchField = () => {
  const { watch, setValue, getValues } = useFormContext<YachtsFiltersFormT>();

  const location = watch("location") || [];
  const country = watch("country") || [];
  const region = watch("region") || [];
  const model = watch("model") || [];
  const tlr = watch("tlr") || [];
  const town = watch("town") || [];

  const value = useMemo(() => {
    const searchArray: { name: string; entityType: "location" | "town" | "model" | "region" | "country" | "tlr" }[] =
      [];
    location.forEach((name: string) => searchArray.push({ name, entityType: "location" }));
    country.forEach((name: string) => searchArray.push({ name, entityType: "country" }));
    region.forEach((name: string) => searchArray.push({ name, entityType: "region" }));
    model.forEach((name: string) => searchArray.push({ name, entityType: "model" }));
    tlr.forEach((name: string) => searchArray.push({ name, entityType: "tlr" }));
    town.forEach((name: string) => searchArray.push({ name, entityType: "town" }));
    return searchArray;
  }, [location, country, region, model, town, tlr]);

  const btnText = value.map((v) => v.name).join(", ") || "Search boat or destination";

  const resetValues = () => {
    setValue("location", [], { shouldDirty: true });
    setValue("country", [], { shouldDirty: true });
    setValue("model", [], { shouldDirty: true });
    setValue("region", [], { shouldDirty: true });
    setValue("tlr", [], { shouldDirty: true });
    setValue("town", [], { shouldDirty: true });
  };

  return (
    <FormItem className="flex flex-col basis-1/4 relative after:content-[''] after:w-[1px] after:h-full after:bg-slate-300 after:flex after:absolute after:top-0 after:-right-4">
      <Popover>
        <div className="relative">
          <PopoverTrigger className="flex flex-col items-start">
            <FormLabel className="text-base font-normal">Where</FormLabel>
            <span
              className={`text-base text-left break-all ${value.length ? "text-black" : "text-gray-400"} !m-0 line-clamp-1 max-w-[200px]`}
            >
              {btnText}
            </span>
          </PopoverTrigger>
          {value.length > 0 && (
            <span className="absolute right-0 top-1/2 -translate-y-1/2 cursor-pointer" onClick={resetValues}>
              <XCircleIcon />
            </span>
          )}
        </div>
        <PopoverContent alignOffset={-24} align="start" className="w-auto">
          <ComboboxYachtsSearch
            className="flex w-96"
            loadOptions={async (query) => {
              const r = await boatsFetch<{
                data: {
                  name: string;
                  entityType: "model" | "location" | "town" | "country" | "region" | "tlr";
                }[];
                success: boolean;
              }>(`/search/locations?target=app${query ? `&query=${query}` : ""}`);
              return r.data as any;
            }}
            placeholder="Type to search..."
            value={value.map((v) => ({ ...v, name: [v.name] }))}
            onChange={(selectedOption) => {
              if (!selectedOption) return;
              const { name, entityType } = selectedOption as any;
              const currentValues = getValues(entityType) || [];
              if (currentValues.includes(name[0])) {
                setValue(
                  entityType,
                  currentValues.filter((n: string) => n !== name[0]),
                  { shouldDirty: true },
                );
              } else {
                setValue(entityType, [...currentValues, name[0]], { shouldDirty: true });
              }
            }}
            renderOption={(r: any) => r.name[0]}
            optionLabel={(r: any) => r.entityType}
            emptyContent={
              <div>
                <div className="text-xs font-medium text-slate-500 mb-2">Popular searches</div>
                <div className="flex flex-wrap gap-2">
                  {POPULAR_SEARCHES.map((p) => {
                    const selected = (watch(p.entityType) || []) as string[];
                    const active = p.names.every((n) => selected.includes(n));
                    return (
                      <button
                        key={p.label}
                        type="button"
                        onClick={() => {
                          const current = (getValues(p.entityType) || []) as string[];
                          const next = active
                            ? current.filter((n) => !p.names.includes(n))
                            : Array.from(new Set([...current, ...p.names]));
                          setValue(p.entityType, next, { shouldDirty: true });
                        }}
                        className={`text-sm rounded-full border px-3 py-1 transition-colors ${
                          active
                            ? "bg-blue-100 border-blue-300 text-blue-800"
                            : "border-slate-200 text-slate-700 hover:bg-slate-50"
                        }`}
                      >
                        {p.label}
                      </button>
                    );
                  })}
                </div>
              </div>
            }
          />
        </PopoverContent>
      </Popover>
    </FormItem>
  );
};

const DateField = () => {
  const { setValue, watch } = useFormContext<YachtsFiltersFormT>();
  const startDate = watch("checkIn");
  const endDate = watch("checkOut");

  const [date, setDate] = useState<DateRange | undefined>(
    startDate && endDate ? defaultDates(startDate, endDate) : undefined,
  );

  const btnText =
    startDate && endDate
      ? `${format(parseISO(startDate), "dd MMM")} - ${format(parseISO(endDate), "dd MMM")}`
      : "Add dates";

  const resetValues = () => {
    setValue("checkIn", "", { shouldDirty: true });
    setValue("checkOut", "", { shouldDirty: true });
    setDate(undefined);
  };

  // useEffect(() => {
  //   if (startDate) {
  //     console.log(date, startDate, format(parseISO(startDate), "dd MMM"));
  //   }
  // }, [startDate]);
  return (
    <FormItem className="flex basis-1/4 relative after:content-[''] after:w-[1px] after:h-full after:bg-slate-300 after:flex after:absolute after:top-0 after:-right-4 z-10">
      <Popover>
        <div className="relative w-full">
          <PopoverTrigger className="flex flex-col w-full items-start">
            <FormLabel className="text-base font-normal">When</FormLabel>
            <span className={`text-base ${startDate && endDate ? "text-black" : "text-gray-400"}`}>{btnText}</span>
          </PopoverTrigger>
          {startDate && endDate && (
            <span className="absolute right-0 top-1/2 -translate-y-1/2" onClick={resetValues}>
              <XCircleIcon />
            </span>
          )}
        </div>
        <PopoverContent className="w-auto p-0 bg-white">
          <Calendar
            initialFocus
            mode="range"
            defaultMonth={date?.from}
            selected={date}
            onSelect={(dates) => {
              setDate(dates);
              if (dates && dates.from && dates.to) {
                setValue("checkIn", format(parseISO(dates.from.toISOString()), "yyyy-MM-dd"), { shouldDirty: true });
                setValue(
                  "checkOut",
                  dates.to
                    ? format(parseISO(dates.to.toISOString()), "yyyy-MM-dd")
                    : format(parseISO(dates.from.toISOString()), "yyyy-MM-dd"),
                  {
                    shouldDirty: true,
                  },
                );
              }
            }}
            numberOfMonths={2}
          />
        </PopoverContent>
      </Popover>
    </FormItem>
  );
};

const GuestsField = () => {
  const { watch, setValue } = useFormContext();

  const people = watch("people");
  const btnText = people > 0 ? `${people} guest${people > 1 ? "s" : ""}` : "Add guests";

  return (
    <FormField<YachtsFiltersFormT, "people">
      name="people"
      render={({ field: { value, onChange } }) => {
        return (
          <Popover>
            <FormItem className="flex flex-col basis-1/4 relative after:content-[''] after:w-[1px] after:h-full after:bg-slate-300 after:flex after:absolute after:top-0 after:-right-4 z-10">
              <div className="relative w-full">
                {people > 1 && (
                  <span
                    className="absolute right-0 top-1/2 -translate-y-1/2"
                    onClick={() => setValue("people", 1, { shouldDirty: true })}
                  >
                    <XCircleIcon />
                  </span>
                )}
                <PopoverTrigger className="flex flex-col w-full items-start">
                  <FormLabel className="text-base font-normal">Who</FormLabel>
                  <span className={`text-base ${people > 1 ? "text-black" : "text-gray-400"}`}>{btnText}</span>
                </PopoverTrigger>
              </div>
              <PopoverContent className="flex flex-col gap-3 w-auto min-w-[260px]">
                <div className="flex items-center justify-between gap-6">
                  <span className="text-sm font-medium">Guests</span>
                  <FormControl>
                    <ScoreSelect min={1} value={value || people} max={20} onChange={onChange} />
                  </FormControl>
                </div>
                <div className="flex items-center justify-between gap-6">
                  <span className="text-sm font-medium">Cabins (min)</span>
                  <ScoreSelect
                    min={0}
                    max={12}
                    value={watch("cabinsMin") ?? 0}
                    onChange={(v) => setValue("cabinsMin", v, { shouldDirty: true })}
                  />
                </div>
                <div className="flex items-center justify-between gap-6">
                  <span className="text-sm font-medium">Cabins (max)</span>
                  <ScoreSelect
                    min={0}
                    max={12}
                    value={watch("cabinsMax") ?? 0}
                    onChange={(v) => setValue("cabinsMax", v, { shouldDirty: true })}
                  />
                </div>
                <div className="flex items-center justify-between gap-6">
                  <span className="text-sm font-medium">Heads (min)</span>
                  <ScoreSelect
                    min={0}
                    max={12}
                    value={watch("bathroomsMin") ?? 0}
                    onChange={(v) => setValue("bathroomsMin", v, { shouldDirty: true })}
                  />
                </div>
                <div className="flex items-center justify-between gap-6">
                  <span className="text-sm font-medium">Heads (max)</span>
                  <ScoreSelect
                    min={0}
                    max={12}
                    value={watch("bathroomsMax") ?? 0}
                    onChange={(v) => setValue("bathroomsMax", v, { shouldDirty: true })}
                  />
                </div>
              </PopoverContent>
            </FormItem>
          </Popover>
        );
      }}
    />
  );
};

const YachtTypesField = () => {
  const types = [
    {
      id: "Sailing yacht",
      label: "Sailing yacht",
    },
    {
      id: "Catamaran",
      label: "Catamaran",
    },
    {
      id: "Do not display",
      label: "Do not display",
    },
    {
      id: "Gulet",
      label: "Gulet",
    },
    {
      id: "Jet ski",
      label: "Jet ski",
    },
    {
      id: "Power catamaran",
      label: "Power catamaran",
    },
    {
      id: "R.I.B / Speed boat",
      label: "R.I.B / Speed boat",
    },
    {
      id: "Other",
      label: "Other",
    },
    {
      id: "Motor yacht",
      label: "Motor yacht",
    },
  ] as const;

  const { watch, setValue } = useFormContext<YachtsFiltersFormT>();
  const type = watch("boatType");

  const btnText = !!type?.length ? type.join(", ") : "Any yacht type";

  return (
    <Popover>
      <FormItem className="flex flex-col basis-1/4 relative z-10">
        <div className="relative w-full">
          {!!type?.length && (
            <span
              className="absolute right-0 top-1/2 -translate-y-1/2"
              onClick={() => setValue("boatType", [], { shouldDirty: true })}
            >
              <XCircleIcon />
            </span>
          )}
          <PopoverTrigger className="flex flex-col w-full items-start">
            <FormLabel className="text-base font-normal">Yacht type</FormLabel>
            <span className={`text-base ${!!type?.length ? "text-black" : "text-gray-400"} line-clamp-1 text-left`}>
              {btnText}
            </span>
          </PopoverTrigger>
        </div>

        <PopoverContent className="flex gap-x-4 flex-col w-auto mr-12">
          <FormField<YachtsFiltersFormT, "boatType">
            name="boatType"
            render={() => (
              <FormItem className="grid grid-cols-2 gap-3 py-4">
                {types.map((item) => (
                  <FormField
                    key={item.id}
                    name="boatType"
                    render={({ field }) => {
                      return (
                        <FormItem key={item.id} className="flex flex-row items-center gap-3 space-y-0 w-max !m-0">
                          <FormControl>
                            <Checkbox
                              className="w-8 h-8"
                              checked={field.value?.includes(item.id)}
                              onCheckedChange={(checked) => {
                                return checked
                                  ? field.onChange([...field.value, item.id])
                                  : field.onChange(field.value?.filter((value: string) => value !== item.id));
                              }}
                            />
                          </FormControl>
                          <FormLabel className="font-normal text-base leading-6">{item.label}</FormLabel>
                        </FormItem>
                      );
                    }}
                  />
                ))}
              </FormItem>
            )}
          />
        </PopoverContent>
      </FormItem>
    </Popover>
  );
};
